/* ==========================================================================
   Bundle upsell
   --------------------------------------------------------------------------
   The theme's own cart engine (assets/interactable.min.js) already:
     - handles [data-action-atc] clicks
     - POSTs to /cart/add.js with sections "cart_data,mini_cart"
     - re-renders the drawer and cart count from the response
     - manages the is--loading / aria-disabled states (double-submit guard)
     - fires "add:cart:success" and "cart:updated" on document

   What this file does:
     1. decides whether an add came from the MAIN product form
     2. if so, reveals the configured upsell (popup or inline block)
     3. when the upsell itself is added, applies that offer's discount code
        to the cart and refreshes the drawer through the theme's own renderer
     4. when the upsell product is ALREADY in the cart without the offer, lets
        the customer claim the offer price on the unit they have, instead of
        adding a second one
     5. closes the popup and enforces the "only one of these" selection limit
     6. shows "ADD TO CART" text when it fits a card, the round icon when not
     7. provides modal a11y - focus trap, Esc, overlay click, focus restore

   It also exposes window.BundleUpsell.show(root) so assets/bundle-offer-cleanup.js
   can show a bundle's upsell after a quick add from a collection grid. A
   two-product block shown that way is turned into a modal, because there is
   no product info column to reveal it in.

   Removing a code that no longer applies (bundle or upsell removed) is
   handled site-wide by assets/bundle-offer-cleanup.js.

   Selection state is derived from the CART, not from browser storage. If the
   customer removes the upsell from the cart, the cards unlock again.
   ========================================================================== */

(function () {
  'use strict';

  // Guard against the asset being included twice on one page.
  if (window.__bundleUpsellReady) return;
  window.__bundleUpsellReady = true;

  var MAIN_FORM = 'form[data-type="add-to-cart-form"]';
  var OPEN_MODAL = '[data-bundle-upsell][data-upsell-type="popup"].is--open';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Button label when the product is already in the cart, so the button
     applies the offer instead of adding another one. */
  var CLAIM_LABEL = 'Claim offer';

  /* Where the pending add-to-cart came from, and for upsell adds the button
     that was clicked. Read and cleared by the add:cart:success handler. */
  var pendingSource = null;
  var pendingButton = null;

  var roots = [];
  var lastFocused = null;
  var listenersReady = false;
  var resizeObserver = null;

  /* What the cart holds right now - the single source of truth for which
     upsells have been taken and which offer codes are already active. */
  var cartVariantIds = [];
  var cartCodes = [];
  var syncTimer = null;

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */

  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if (el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function cards(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-upsell-card]'));
  }

  function limitOf(root) {
    return parseInt(root.getAttribute('data-upsell-limit'), 10) || 1;
  }

  function isModal(root) {
    return root.getAttribute('data-upsell-type') === 'popup';
  }

  function codeOf(card) {
    var btn = card.querySelector('[data-upsell-add]');
    return (btn && btn.getAttribute('data-discount-code')) || card.getAttribute('data-discount-code') || '';
  }

  /* No code configured counts as applied: there is nothing left to claim. */
  function isCodeApplied(code) {
    return !code || cartCodes.indexOf(String(code).toUpperCase()) !== -1;
  }

  /* Which of this block's variants are actually in the cart right now. */
  function takenIn(root) {
    return cards(root)
      .map(function (card) {
        return card.getAttribute('data-variant-id');
      })
      .filter(function (id) {
        return cartVariantIds.indexOf(id) !== -1;
      });
  }

  function cartBase() {
    return (window.T4Sroutes && window.T4Sroutes.cart_url) || '/cart';
  }

  /* Re-render the drawer and cart count using the theme's own renderer. */
  function refreshCart() {
    try {
      if (window.T4SThemeSP && T4SThemeSP.Cart && typeof T4SThemeSP.Cart.getToFetch === 'function') {
        T4SThemeSP.Cart.getToFetch();
      }
    } catch (e) {
      /* theme API changed - the discount still applies at checkout */
    }
  }

  /* ---------------------------------------------------------------------
     Cart sync
     --------------------------------------------------------------------- */

  function syncFromCart() {
    return fetch(cartBase() + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        cartVariantIds = (cart.items || []).map(function (item) {
          return String(item.variant_id);
        });
        // Only codes Shopify is actually honouring count as applied.
        cartCodes = (cart.discount_codes || [])
          .filter(function (d) {
            return d.applicable !== false;
          })
          .map(function (d) {
            return String(d.code).toUpperCase();
          });
        roots.forEach(applyLimit);
      })
      .catch(function () {
        /* Network hiccup - leave the current state alone rather than
           wrongly unlocking or locking the cards. */
      });
  }

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFromCart, 250);
  }

  /* ---------------------------------------------------------------------
     Offer discount code
     --------------------------------------------------------------------- */

  /* Add the offer's code to the cart ALONGSIDE any codes already there - the
     cart update replaces the whole list, so existing codes are re-sent. The
     code itself is a Buy X Get Y discount, so Shopify only honours it while
     the bundle is in the cart; the theme never decides the price. */
  function applyDiscountCode(code) {
    code = String(code || '').trim();
    if (!code) return Promise.resolve();

    // Tells bundle-offer-cleanup.js not to touch the code list mid-update.
    window.__bundleOfferApplying = Date.now();

    return fetch(cartBase() + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var codes = (cart.discount_codes || []).map(function (d) {
          return String(d.code);
        });
        var already = codes.some(function (c) {
          return c.toUpperCase() === code.toUpperCase();
        });
        if (already) return null;

        codes.push(code);
        return fetch(cartBase() + '/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: codes.join(',') })
        });
      })
      .then(function () {
        refreshCart();
      })
      .catch(function () {
        /* If this fails the product is still in the cart at full price -
           never undercharged. */
      })
      .then(function () {
        window.__bundleOfferApplying = Date.now();
      });
  }

  /* ---------------------------------------------------------------------
     Card states and the "only one of these" selection limit
     --------------------------------------------------------------------- */

  function applyLimit(root) {
    var limit = limitOf(root);
    var taken = takenIn(root);

    cards(root).forEach(function (card) {
      var id = card.getAttribute('data-variant-id');
      var btn = card.querySelector('[data-upsell-add]');
      var label = card.querySelector('[data-upsell-atc-text]');
      var inCart = taken.indexOf(id) !== -1;

      /* Already in the cart, but this offer's code is not active yet: the
         customer can still claim the offer price on the unit they have, so
         keep the button live and have it apply the code instead of adding a
         second one (the Buy X Get Y code only ever discounts one unit). */
      var claimable = inCart && !isCodeApplied(codeOf(card));
      var settled = inCart && !claimable;
      var locked = !inCart && taken.length >= limit;

      card.classList.toggle('is--added', settled);
      card.classList.toggle('is--claim', claimable);
      card.classList.toggle('is--locked', locked);

      if (!btn) return;

      if (claimable) {
        // No data-action-atc, so the theme's cart engine does not add a unit.
        btn.removeAttribute('data-action-atc');
        btn.setAttribute('data-upsell-claim', '');
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      } else if (settled || locked) {
        // Removing the attribute detaches the theme's delegated cart handler,
        // so a settled or locked card genuinely cannot add anything.
        btn.removeAttribute('data-action-atc');
        btn.removeAttribute('data-upsell-claim');
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else {
        btn.setAttribute('data-action-atc', '');
        btn.removeAttribute('data-upsell-claim');
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      }

      if (label) {
        if (!label.getAttribute('data-default-label')) {
          label.setAttribute('data-default-label', label.textContent);
        }
        label.textContent = settled
          ? 'Added'
          : claimable
          ? CLAIM_LABEL
          : label.getAttribute('data-default-label');
      }
    });
  }

  /* Optimistic update so the UI responds instantly; the cart sync that
     follows confirms it. */
  function markAdded(variantId, code) {
    if (variantId && cartVariantIds.indexOf(variantId) === -1) {
      cartVariantIds.push(variantId);
    }
    if (code && cartCodes.indexOf(String(code).toUpperCase()) === -1) {
      cartCodes.push(String(code).toUpperCase());
    }
    roots.forEach(applyLimit);
  }

  /* ---------------------------------------------------------------------
     Button fit - "ADD TO CART" text when it fits, the round icon when not
     --------------------------------------------------------------------- */

  /* With the text showing, add up what the button needs - the full text width,
     the icon, the gap and the padding - and compare it with the width the
     button actually has. If it needs more, the text is being cut off, so
     switch that card to icon-only. (A button's own scrollWidth is not a
     reliable overflow signal, so the parts are measured directly.) Only the
     two-product block uses this; popup cards always have room. */
  function fitButton(card) {
    if (!closest(card, '.bundle-upsell--product_block')) return;
    var btn = card.querySelector('[data-upsell-add]');
    var text = card.querySelector('.bundle-upsell__atc-text');
    var icon = card.querySelector('.bundle-upsell__atc-icon');
    if (!btn || !text || !icon || !card.offsetWidth) return; // hidden - fitted once revealed

    card.classList.remove('is--icon-only');

    var style = window.getComputedStyle(btn);
    var needed =
      text.scrollWidth +
      icon.offsetWidth +
      (parseFloat(style.columnGap || style.gap) || 0) +
      (parseFloat(style.paddingLeft) || 0) +
      (parseFloat(style.paddingRight) || 0);

    if (needed > btn.clientWidth + 0.5) {
      card.classList.add('is--icon-only');
    }
  }

  function fitButtons(root) {
    cards(root).forEach(fitButton);
  }

  /* Re-fit whenever a card changes size - rotation, window resize, or going
     from hidden to shown. */
  function observeCards(root) {
    if (!window.ResizeObserver) return;
    if (!resizeObserver) {
      resizeObserver = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          fitButton(entry.target);
        });
      });
    }
    cards(root).forEach(function (card) {
      resizeObserver.observe(card);
    });
  }

  /* ---------------------------------------------------------------------
     Popup / modal open and close
     --------------------------------------------------------------------- */

  function trapFocus(root, event) {
    if (event.key !== 'Tab') return;
    var items = root.querySelectorAll(FOCUSABLE);
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    var root = document.querySelector(OPEN_MODAL);
    if (!root) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      closePopup(root);
      return;
    }
    trapFocus(root, event);
  }

  function openPopup(root) {
    if (root.classList.contains('is--open')) return;

    lastFocused = document.activeElement;

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');

    // Force a synchronous reflow so the transition has a start state to run
    // from. requestAnimationFrame is throttled to zero in background tabs,
    // which would leave the popup displayed at opacity 0.
    void root.offsetWidth;
    root.classList.add('is--open');
    fitButtons(root);

    var target =
      root.querySelector('[data-upsell-add]:not([disabled])') ||
      root.querySelector('[data-upsell-close]');
    if (target) target.focus();
  }

  function closePopup(root) {
    if (!root.classList.contains('is--open')) return;

    root.classList.remove('is--open');
    root.setAttribute('aria-hidden', 'true');

    window.setTimeout(function () {
      if (!root.classList.contains('is--open')) root.hidden = true;
    }, 280);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* A two-product inline block injected outside its product page has no
     product column to live in, so present it as a modal: overlay, dialog
     semantics and a close button. */
  function convertToModal(root) {
    root.classList.add('bundle-upsell--modal');
    root.setAttribute('data-upsell-type', 'popup');
    root.setAttribute('aria-hidden', 'true');
    root.hidden = true;

    if (!root.querySelector('.bundle-upsell__overlay')) {
      var overlay = document.createElement('div');
      overlay.className = 'bundle-upsell__overlay';
      overlay.setAttribute('data-upsell-close', '');
      overlay.setAttribute('data-upsell-close-region', '');
      overlay.tabIndex = -1;
      root.insertBefore(overlay, root.firstChild);
    }

    var items = root.querySelector('.bundle-upsell__items');
    if (!items) return;

    var title = items.querySelector('.bundle-upsell__title');
    items.setAttribute('role', 'dialog');
    items.setAttribute('aria-modal', 'true');
    if (title && title.id) items.setAttribute('aria-labelledby', title.id);

    if (!items.querySelector('.bundle-upsell__modal-close')) {
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'bundle-upsell__modal-close';
      close.setAttribute('data-upsell-close', '');
      close.setAttribute('aria-label', 'Close');
      close.innerHTML =
        '<svg viewBox="0 0 16 16" focusable="false" role="presentation" aria-hidden="true">' +
        '<path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>';
      items.appendChild(close);
    }
  }

  /* ---------------------------------------------------------------------
     Reveal - called after the BUNDLE itself is added
     --------------------------------------------------------------------- */

  /* Is there anything left to offer? A card is finished when it is in the
     cart AND its offer is already applied. A product that is in the cart
     WITHOUT the offer still counts - the customer can claim the offer price
     on the unit they already have. */
  function hasOffer(root) {
    var limit = limitOf(root);
    var taken = takenIn(root);

    return cards(root).some(function (card) {
      var inCart = taken.indexOf(card.getAttribute('data-variant-id')) !== -1;
      if (inCart) return !isCodeApplied(codeOf(card));
      return taken.length < limit;
    });
  }

  function reveal(root) {
    if (!hasOffer(root)) return;

    if (isModal(root)) {
      openPopup(root);
    } else {
      root.hidden = false;
      root.classList.add('is--visible');
      fitButtons(root);
    }
  }

  /* ---------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------- */

  function registerRoot(root) {
    if (roots.indexOf(root) !== -1) return;

    var injected = root.hasAttribute('data-upsell-injected');
    if (injected && root.getAttribute('data-upsell-type') === 'product_block') {
      convertToModal(root);
    }

    // A fixed-position modal inside a transformed ancestor is positioned
    // against that ancestor, not the viewport. The product info column uses
    // reveal animations, so move popups to <body> to be safe.
    if (isModal(root) && root.parentElement !== document.body) {
      document.body.appendChild(root);
    }

    root.addEventListener('click', function (event) {
      if (closest(event.target, '[data-upsell-close]') || closest(event.target, '[data-upsell-close-region]')) {
        event.preventDefault();
        closePopup(root);
        return;
      }

      /* Claim: the product is already in the cart, so apply the offer code
         rather than adding a second one. */
      var claim = closest(event.target, '[data-upsell-claim]');
      if (!claim) return;

      event.preventDefault();
      if (claim.classList.contains('is--loading')) return;

      var card = closest(claim, '[data-upsell-card]');
      var code = card ? codeOf(card) : '';
      claim.classList.add('is--loading');

      applyDiscountCode(code).then(function () {
        claim.classList.remove('is--loading');
        markAdded(card && card.getAttribute('data-variant-id'), code);
        if (isModal(root)) closePopup(root);
        scheduleSync();
      });
    });

    roots.push(root);
    applyLimit(root);
    observeCards(root);
  }

  function ensureListeners() {
    if (listenersReady) return;
    listenersReady = true;

    // Clear locks written by an earlier version of this script, which used
    // sessionStorage and could not tell that the cart had been emptied.
    try {
      Object.keys(sessionStorage)
        .filter(function (k) {
          return k.indexOf('bundle-upsell:') === 0;
        })
        .forEach(function (k) {
          sessionStorage.removeItem(k);
        });
    } catch (e) {
      /* storage unavailable - nothing to clean up */
    }

    document.addEventListener('keydown', onKeydown);

    // Browsers without ResizeObserver: re-fit the buttons on window resize.
    if (!window.ResizeObserver) {
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(function () {
          roots.forEach(fitButtons);
        }, 150);
      });
    }

    /* Record the origin of every add, in the capture phase so we run before
       the theme's own delegated handlers. */
    document.addEventListener(
      'click',
      function (event) {
        var upsellBtn = closest(event.target, '[data-upsell-add]');
        if (upsellBtn) {
          // A claim adds nothing, so there is no add to attribute to it.
          if (upsellBtn.hasAttribute('data-upsell-claim')) {
            pendingSource = null;
            pendingButton = null;
          } else {
            pendingSource = 'upsell';
            pendingButton = upsellBtn;
          }
        } else if (closest(event.target, MAIN_FORM)) {
          pendingSource = 'main';
          pendingButton = null;
        } else if (closest(event.target, '[data-action-atc]') || closest(event.target, 'form[action*="/cart/add"]')) {
          // FBT, quick-add, complementary products - not the main-form trigger.
          // Quick adds of a bundle are handled by bundle-offer-cleanup.js.
          pendingSource = 'other';
          pendingButton = null;
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      function (event) {
        if (closest(event.target, MAIN_FORM)) {
          pendingSource = 'main';
          pendingButton = null;
        }
      },
      true
    );

    /* Re-read the cart whenever it changes, so removing an upsell unlocks the
       cards again. The theme fires cart:updated after adds, but its drawer
       remove / quantity buttons fire nothing on success - the site-wide
       bundle-offer-cleanup.js watches the cart requests and announces those. */
    document.addEventListener('bundle-offer:cart-changed', scheduleSync);

    var $ = window.jQuery;
    if (!$) return;

    $(document).on('add:cart:success.bundleUpsell', function () {
      var source = pendingSource;
      var btn = pendingButton;
      pendingSource = null;
      pendingButton = null;

      if (source === 'upsell') {
        var card = btn ? closest(btn, '[data-upsell-card]') : null;
        if (card) {
          var code = btn.getAttribute('data-discount-code') || card.getAttribute('data-discount-code');
          markAdded(card.getAttribute('data-variant-id'), code);
          applyDiscountCode(code);
        }

        roots.forEach(function (root) {
          if (isModal(root)) closePopup(root);
        });
        return;
      }

      // Main product form: reveal this page's own upsell, never one that was
      // injected for a quick-added bundle.
      if (source === 'main') {
        roots.forEach(function (root) {
          if (!root.hasAttribute('data-upsell-injected')) reveal(root);
        });
      }
    });

    $(document).on('cart:updated.bundleUpsell', scheduleSync);
  }

  /* Public entry point for bundle-offer-cleanup.js: register a root injected
     after page load, sync with the cart, then show it. */
  window.BundleUpsell = {
    show: function (root) {
      registerRoot(root);
      ensureListeners();
      return syncFromCart().then(function () {
        reveal(root);
        return root;
      });
    }
  };

  function init() {
    var found = Array.prototype.slice.call(document.querySelectorAll('[data-bundle-upsell]'));
    if (!found.length) return;

    found.forEach(registerRoot);
    ensureListeners();
    syncFromCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
